import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SymptomLogModal = ({ isOpen, onClose, onSave }) => {
  const [severity, setSeverity] = useState(5);
  const [notes, setNotes] = useState('');
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);

  const commonSymptoms = [
    { id: 'headache', label: 'Headache', icon: 'Zap' },
    { id: 'fatigue', label: 'Fatigue', icon: 'Battery' },
    { id: 'dizziness', label: 'Dizziness', icon: 'RotateCcw' },
    { id: 'memory', label: 'Memory Issues', icon: 'Brain' },
    { id: 'coordination', label: 'Coordination', icon: 'Target' },
    { id: 'mood', label: 'Mood Changes', icon: 'Heart' }
  ];

  const handleSymptomToggle = (symptomId) => {
    setSelectedSymptoms(prev => 
      prev?.includes(symptomId) 
        ? prev?.filter(id => id !== symptomId)
        : [...prev, symptomId]
    );
  };

  const handleSave = () => {
    const logData = {
      severity,
      notes,
      symptoms: selectedSymptoms,
      timestamp: new Date()?.toISOString(),
      date: new Date()?.toLocaleDateString()
    };
    onSave(logData);
    setSeverity(5);
    setNotes('');
    setSelectedSymptoms([]);
    onClose();
  };

  const getSeverityColor = (value) => {
    if (value <= 3) return 'text-success';
    if (value <= 6) return 'text-warning';
    return 'text-error';
  };

  const getSeverityLabel = (value) => {
    if (value <= 2) return 'Minimal';
    if (value <= 4) return 'Mild';
    if (value <= 6) return 'Moderate';
    if (value <= 8) return 'Severe';
    return 'Extreme';
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[1100]" onClick={onClose} />
      <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 md:w-96 md:max-w-lg bg-card border border-border rounded-lg shadow-soft-lg z-[1100] animate-scale-in max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">Log Symptoms</h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <Icon name="X" size={18} />
            </Button>
          </div>

          {/* Symptom Selection */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-foreground mb-3">Select Symptoms</h3>
            <div className="grid grid-cols-2 gap-2">
              {commonSymptoms?.map((symptom) => (
                <button
                  key={symptom?.id}
                  onClick={() => handleSymptomToggle(symptom?.id)}
                  className={`
                    flex items-center space-x-2 p-3 rounded-lg border transition-all duration-150
                    ${selectedSymptoms?.includes(symptom?.id)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted text-foreground border-border hover:bg-muted/80'
                    }
                  `}
                >
                  <Icon name={symptom?.icon} size={16} />
                  <span className="text-sm font-medium">{symptom?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Severity Slider */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-foreground">Severity Level</h3>
              <div className={`text-lg font-semibold ${getSeverityColor(severity)}`}>
                {severity}/10 - {getSeverityLabel(severity)}
              </div>
            </div>
            
            <div className="relative">
              <input
                type="range"
                min="1"
                max="10"
                value={severity}
                onChange={(e) => setSeverity(parseInt(e?.target?.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>1 - Minimal</span>
                <span>5 - Moderate</span>
                <span>10 - Extreme</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <Input
              label="Additional Notes (Optional)"
              type="text"
              placeholder="Describe your symptoms, triggers, or any additional details..."
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              className="min-h-[80px] resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              disabled={selectedSymptoms?.length === 0}
              className="flex-1"
            >
              Save Symptoms
            </Button>
          </div>
        </div>
      </div>
      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: var(--color-primary);
          cursor: pointer;
          border: 2px solid var(--color-background);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: var(--color-primary);
          cursor: pointer;
          border: 2px solid var(--color-background);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
      `}</style>
    </>
  );
};

export default SymptomLogModal;